import {
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Typography,
  Button,
  IconButton,
  Switch,
  Alert,
} from '@mui/material';
import { ContentCopy, CheckOutlined, Delete, Add } from '@mui/icons-material';
import { useCallback, useState, FocusEvent } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import copy from 'copy-to-clipboard';
import { Field, FieldArray, useField, useFormikContext } from 'formik';

import { api } from '../../api';
import { getUserWidgetUrl, YM_COUNTER } from '../../config';
import { useAppSelector, useGetCampaignByUrl } from '../../lib';
import { CheckboxField, FormRowsDivider, InputField } from '../fields';
import { Section, SectionTitle } from './components';
import { configSelectors } from '../../store';

export const Publish = () => {
  const { t } = useTranslation();
  const [publish, publishResult] = api.usePublishCampaignMutation();
  const campaign = useGetCampaignByUrl();
  const isStandalone = useAppSelector(configSelectors.isStandalone);
  const [copied, setCopied] = useState(false);

  const [{ value: showOnAllSites }] = useField<boolean>('campaign.showOnAllSites');
  const { submitForm, setFieldValue, validateForm } = useFormikContext();

  const handlePublish = useCallback(() => {
    if (campaign?.id) {
      publish({ id: campaign?.id, publish: !campaign.active });

      if (!campaign.active) {
        ym(YM_COUNTER, 'reachGoal', 'publish_campaign');
      }
    }
  }, [campaign, publish]);

  const handlePublishOnDirectLink = useCallback(() => {
    if (campaign?.id) {
      publish({ id: campaign?.id, publishOnDirectLink: !campaign.activeOnDirectLink });

      if (!campaign.activeOnDirectLink) {
        ym(YM_COUNTER, 'reachGoal', 'publish_campaign');
      }
    }
  }, [campaign, publish]);

  const validate = (value: string, schema: Yup.AnySchema) => {
    try {
      schema.validateSync(value);
    } catch (error) {
      return (error as any).message;
    }
  };

  if (!campaign) {
    return null;
  }

  const widgetUrl = getUserWidgetUrl(campaign);

  const allowedSitesSliceName = 'campaign.allowedSites.sites';

  return (
    <Wrapper>
      <Section elevation={0}>
        <SectionTitle>{t('pages.campaign.publish.title')}</SectionTitle>

        <FormGroup>
          <FormControlLabel
            className="form-field"
            disabled={publishResult.isLoading}
            control={<Switch checked={campaign.active} onChange={handlePublish} disabled={publishResult.isLoading} />}
            label={
              <CheckboxLabel>
                {campaign.active ? t('buttons.publishedOnSite') : t('buttons.publishOnSite')}
                {publishResult.isLoading && <CircularProgress size={16} />}
              </CheckboxLabel>
            }
          />

          {isStandalone && campaign.active && (
            <>
              <Field
                name="campaign.showOnAllSites"
                component={CheckboxField}
                label="Показывать на всех сайтах, где установлен код виджета"
                helperText="Показывать на всех сайтах, где установлен код виджета"
                className="form-field"
              />

              {!showOnAllSites && (
                <>
                  <Typography sx={{ marginBottom: '12px' }} variant="caption">
                    Показывать только на следующих сайтах:
                  </Typography>

                  <SitesWrap>
                    <FieldArray name={allowedSitesSliceName}>
                      {({ remove, push, form }) => {
                        const sites = (form.values.campaign.allowedSites?.sites || []) as string[];

                        return (
                          <>
                            {sites.map((site, index) => {
                              const fieldName = `${allowedSitesSliceName}[${index}]`;

                              return (
                                <SiteWrap>
                                  <Field
                                    name={fieldName}
                                    component={InputField}
                                    label="URL сайта"
                                    key={index}
                                    submitOnBlur={false}
                                    onBlur={(event: FocusEvent<HTMLInputElement>) => {
                                      const value = event.target.value;

                                      if (!value) {
                                        return;
                                      }

                                      try {
                                        const url = new URL(value);

                                        setFieldValue(fieldName, url.origin).then(() => {
                                          submitForm();
                                        });
                                      } catch (error) {
                                        validateForm();
                                      }
                                    }}
                                    validate={(value: string) =>
                                      validate(value, Yup.string().url('Введите полный адрес сайта'))
                                    }
                                  />

                                  <IconButton
                                    title="Удалить сайт"
                                    onClick={() => {
                                      remove(index);
                                      submitForm();
                                    }}
                                  >
                                    <Delete />
                                  </IconButton>
                                </SiteWrap>
                              );
                            })}

                            {sites.length === 0 ? (
                              <Button
                                onClick={() => {
                                  push('');
                                  submitForm();
                                }}
                                sx={{ marginLeft: '-4px' }}
                              >
                                Добавить сайт
                              </Button>
                            ) : (
                              <IconButton
                                title="Добавить сайт"
                                onClick={() => {
                                  push('');
                                  submitForm();
                                }}
                              >
                                <Add />
                              </IconButton>
                            )}
                          </>
                        );
                      }}
                    </FieldArray>
                  </SitesWrap>
                </>
              )}

              <Alert variant="filled" severity="success" sx={{ marginBottom: '24px' }}>
                {showOnAllSites ? (
                  <>Виджет будет показан на всех сайтах, на которые вы добавите код виджета</>
                ) : (
                  <>Виджет будет показан только на тех сайтах, которые вы указали</>
                )}
              </Alert>
            </>
          )}

          <FormRowsDivider />

          <FormControlLabel
            disabled={publishResult.isLoading}
            control={
              <Switch
                checked={campaign.activeOnDirectLink}
                onChange={handlePublishOnDirectLink}
                disabled={publishResult.isLoading}
              />
            }
            label={
              <CheckboxLabel>
                {campaign.activeOnDirectLink ? t('buttons.publishedOnDirectLink') : t('buttons.publishOnDirectLink')}
                {publishResult.isLoading && <CircularProgress size={16} />}
              </CheckboxLabel>
            }
          />

          <WidgetOnDirectLinkeDescription>
            <span>
              <Typography variant="caption">{t('pages.campaign.publish.publishOnDirectLinkDescription')}</Typography>{' '}
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {widgetUrl}
              </Typography>
            </span>

            <Button
              size="small"
              onClick={() => {
                setCopied(true);
                copy(widgetUrl);
                setTimeout(() => {
                  setCopied(false);
                }, 1000);
              }}
              endIcon={copied ? <CheckOutlined fontSize="small" /> : <ContentCopy fontSize="small" />}
            >
              {t('buttons.copy')}
            </Button>
          </WidgetOnDirectLinkeDescription>
        </FormGroup>
      </Section>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  .form-field {
    margin-bottom: 16px;
  }
`;

const CheckboxLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const WidgetOnDirectLinkeDescription = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  button {
    margin-top: 8px;
    margin-left: -4px;
  }
`;

const SitesWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  align-items: flex-start;
`;

const SiteWrap = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
`;
